import React from "react";
import { formatDpPct } from "lib/numberFormat";
import { _t, i18n } from "app/i18n/i18n";
import { getQueryString, setSort } from "pages/search/api/filterUrlMaker";
import { Link } from "react-router-dom";
import { uuid } from "lib/utils";
import {
  formatRating,
  formatPrice,
  formatDate,
  parseMarketCap,
  analystLink,
  companyLink,
} from "../formatters/formatters";
import { SimpleTable } from "../../../../lib/SimpleTable";
import "./CoverageTable.scss";

export function CoverageTable({ data }) {
  const defaultContainerStyle = {
    height: 700,
    borderLeft: "1px solid gray",
    borderBottom: "1px solid gray",
    borderRight: "1px solid gray",
  };

  const defaultTableStyle = {
    marginBottom: 0,
  };

  return (
    <SimpleTable
      {...data}
      containerStyle={defaultContainerStyle}
      tableStyle={defaultTableStyle}
      bodyRows={CoverageRows}
      headerRow={CoverageHeader}
    />
  );
}

function CoverageHeader() {
  let { SortAsc } = getQueryString();
  const { SortDesc } = getQueryString();
  if (!SortAsc && !SortDesc) SortAsc = defaultSortField;
  const toggleSort = (field) => () => (!(SortAsc === field) ? sortAsc(field) : sortDesc(field));
  function getSortCaret(field) {
    if (field === SortAsc) return sortAscCaret;
    if (field === SortDesc) return sortDescCaret;
    return null;
  }
  const sortCaret = (field) => getSortCaret(field, SortAsc, SortDesc, sortAscCaret, sortDescCaret);

  const headers = [
    { field: "companyName.en", text: "Company Name", className: "sticky-col font-weight-bold" },
    { field: "currentRating", text: "Rating" },
    { field: "price", text: "Price" },
    { field: "targetPrice", text: "Target Price" },
    { field: "currency", text: "Currency" },
    {
      field: "percentageTPChange",
      text: "TP vs MP (%)",
      className: "d-none d-md-table-cell",
      title: "The percentage difference between target and market price.",
    },
    { field: "priceDate", text: "Price Date" },
    { field: "coveredBy.en", text: "Primary Analyst" },
    { field: "countryISO", text: "Country", className: "d-none d-md-table-cell" },
    { field: "ticker", text: "BBG Code", className: "d-none d-md-table-cell" },
    { field: "ric", text: "RIC", className: "d-none d-md-table-cell" },
    { field: "priceHigh52", text: "52-Week High", className: "d-none d-md-table-cell pointer-events-none" },
    { field: "priceLow52", text: "52-Week Low", className: "d-none d-md-table-cell pointer-events-none" },
    { field: "marketCap", text: "Market Cap \n(JPY mn)", className: "d-none d-md-table-cell" },
    { field: "marketCapUSD", text: "Market Cap \n(USD mn)", className: "d-none d-md-table-cell" },
    { field: "avg3MTurnOver", text: "Daily turnover \n(3-M avg) \n(JPY mn)", className: "d-none d-md-table-cell" },
    { field: "avg3MTurnOverUSD", text: "Daily turnover \n(3-M avg) \n(USD mn)", className: "d-none d-md-table-cell" },
    { field: "gicsLevel1Sectors.en", text: "Sector", className: "d-none d-md-table-cell" },
    { field: "gicsLevel2IndustryGroups.en", text: "Sub-Sector", className: "d-none d-md-table-cell" },
  ];

  return (
    <tr className="lg-td-padding cursor-pointer table-rows" lang={i18n.getLanguage()}>
      {headers.map(({ field, text, className }) => (
        <th key={field} onClick={toggleSort(field)} className={className}>
          {sortCaret(field)}
          {_t(text)}
        </th>
      ))}
    </tr>
  );
}

export function CoverageRows({ rows }) {
  const lang = i18n.getLanguage();

  return rows.map((row, idx) => {
    const trKey = uuid(`${idx}-${row.companyName.en}`);

    return (
      <tr key={trKey} className="table-rows" lang={i18n.getLanguage()}>
        <th className="sticky-col">
          <Link to={companyLink(row.companyName && row.companyName[lang], row.ticker)}>
            {row.companyName && row.companyName[lang]}
          </Link>
        </th>
        <td>{formatRating(row.currentRating)}</td>
        <td align="right">{formatPrice(row.price, row.currency)}</td>
        <td align="right">{formatPrice(row.targetPrice, row.currency)}</td>
        <td align="center">{row.currency}</td>
        <td className="d-none d-md-table-cell" align="right">
          {formatDpPct(row.percentageTPChange, 2)}
        </td>
        <td className="price-date">{formatDate(row.priceDate)}</td>
        <td>
          {" "}
          <Link to={analystLink(row.coveredBy.en)}>{row.coveredBy && row.coveredBy[lang]}</Link>{" "}
        </td>
        <td className="d-none d-md-table-cell">{row.countryISO}</td>
        <td className="d-none d-md-table-cell" align="center">
          {row.ticker}
        </td>
        <td className="d-none d-md-table-cell" align="center">
          {row.ric}
        </td>
        <td className="d-none d-md-table-cell" align="right">
          {row.priceHigh52}
        </td>
        <td className="d-none d-md-table-cell" align="right">
          {row.priceLow52}
        </td>
        <td className="d-none d-md-table-cell" align="right">
          {parseMarketCap(row.marketCap)}
        </td>
        <td className="d-none d-md-table-cell" align="right">
          {parseMarketCap(row.marketCapUSD)}
        </td>
        <td className="d-none d-md-table-cell" align="right">
          {parseMarketCap(row.avg3MTurnOver)}
        </td>
        <td className="d-none d-md-table-cell" align="right">
          {parseMarketCap(row.avg3MTurnOverUSD)}
        </td>
        <td className="d-none d-md-table-cell">{row.gicsLevel1Sectors && row.gicsLevel1Sectors[lang]}</td>
        <td className="d-none d-md-table-cell">{row.gicsLevel2IndustryGroups && row.gicsLevel2IndustryGroups[lang]}</td>
      </tr>
    );
  });
}

const defaultSortField = "companyName.en";
const sortAscCaret = "▲\u00A0";
const sortDescCaret = "▼\u00A0";

const sortAsc = (field) => setSort({ SortAsc: field }); //  TODO removed page from query string to avoid issue in pubtab
const sortDesc = (field) => setSort({ SortDesc: field }); // TODO removed page from query string to avoid issue in pubtab
